import {AppUrls} from 'shared/config/model/urls';

import React from 'react';
import {Link} from 'react-router-dom';
import {Control, UseFormRegister} from 'react-hook-form';
import cn from 'classnames';
import {Trans} from '@lingui/react';
import {t} from '@lingui/core/macro';

import Checkbox from 'shared/ui/Checkbox/Checkbox';
import {additionalEmailAPI, isIdSelected, NotifyDelaySelector} from 'entities/notification';
import {HintPremium} from 'entities/tariff';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import Problem from 'shared/ui/icons/General/Problem';
import Envelope from 'shared/ui/icons/Envelope';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import useNotifyChannel from 'features/http/model/useNotifyChannel';

import {PALETTE} from 'shared/config/model/palette';

import styles from './NotifyExtraEmail.module.scss';

interface INotifyExtraEmailProps {
    item?: IHttpMonitorOutput,
    control: Control<any>,
    register: UseFormRegister<any>,
}

const NotifyExtraEmail: React.FC<INotifyExtraEmailProps> = ({item, control, register}) => {
    // eslint-disable-next-line max-len
    const EXTRA_EMAIL = t`Для отправки уведомлений о сбоях нужно подтвердить дополнительный адрес. После подтверждения вы сможете выбрать его в настройках монитора.`;

    const {data: additionalEmails, isLoading} = additionalEmailAPI.useGetActiveQuery();
    const {mode, selected: selectedEmails} = useNotifyChannel(
        control,
        'additional_emails',
        isLoading,
        additionalEmails,
        true,
    );

    return (
        <div className={styles.container}>
            {
                mode === 'free' && (
                    <div className={cn(styles.content, styles.freeTariff)}>
                        <HintPremium condition={'additional_emails_max_count_limit'}/>
                        <span className={styles.text}>
                            <Trans id="text.subtitle.extra.email" message="Уведомления на доп. E-mail не подключены"/>
                        </span>
                        <span className={styles.tooltip}>
                            <HintQuestion>{EXTRA_EMAIL}</HintQuestion>
                        </span>
                    </div>
                )
            }
            {
                mode === 'connect' && (
                    <div className={styles.blockAction}>
                        <div className={styles.content}>
                            <Problem color={PALETTE.neutral}/>
                            <span className={styles.text}>
                                {/* eslint-disable-next-line max-len */}
                                <Trans id="text.subtitle.extra.email" message="Уведомления на доп. E-mail не подключены"/>
                            </span>
                        </div>
                        <div className={styles.action}>
                            <Link
                                className={cn(
                                    styles.link,
                                    styles.colorLink,
                                )}
                                to={AppUrls.SETTINGS}
                            >
                                {t`Подключить`}
                            </Link>
                            <HintQuestion placement="top-start">{EXTRA_EMAIL}</HintQuestion>
                        </div>
                    </div>
                )
            }
            {
                mode === 'list' && (
                    <div>
                        <div className={styles.subHeader}>
                            <Envelope color={PALETTE.neutral}/>
                            <span className={styles.title}>
                                <Trans id="text.title.extra.email" message="Дополнительные E-mail"/>
                            </span>
                        </div>
                        <div className={styles.extraEmailsList}>
                            {
                                (additionalEmails ?? []).map((email, index) => {
                                    const isChecked = isIdSelected(selectedEmails, email.id);

                                    return (
                                        <div key={email.id} className={styles.contentNotFree}>
                                            <Checkbox
                                                textStyle={styles.checkbox}
                                                value={`${email.id}`}
                                                {...register('additional_emails')}
                                            >
                                                {email.description ?? email.email}
                                            </Checkbox>
                                            {
                                                isChecked && (
                                                    <NotifyDelaySelector
                                                        settingsField="notify_additional_emails_settings"
                                                        control={control}
                                                        register={register}
                                                        values={item?.notify_additional_emails_settings ?? []}
                                                        itemId={email.id}
                                                        index={index}
                                                    />
                                                )}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default NotifyExtraEmail;